import { z } from 'zod';
import {
  BOARD_CARD_KEY_MAX_LENGTH,
  BOARD_CARD_KEY_PATTERN,
  SLUG_MAX_LENGTH,
  parseCardRef,
} from './address';
import { boardBackgroundPutSchema } from './board-background';
import { ulidSchema } from './id';
import {
  USERNAME_MAX_LENGTH,
  emailSchema,
  publicProfileSchema,
  usernameSchema,
} from './user';

/**
 * Boards — the contract layer for blocks B08, B09, B10 and (in three places
 * only) B38.
 *
 * B08: `FR-BOARD-001` (create), `FR-BOARD-003` (rename), `FR-EXP-012` (close,
 * reopen, permanently delete — which absorbs `FR-BOARD-007` and `FR-BOARD-008`),
 * `FR-EXP-015` (short links) and `FR-CARD-005` (card addressing).
 *
 * B09: `FR-PERM-008`–`FR-PERM-010` (who may *see* a board — private,
 * workspace-visible, public), `FR-PERM-012` (changing that, board admins only,
 * with the world-readable consequence stated at the moment of confirmation) and
 * `FR-PERM-015` (board roles — admin and normal member — and the member list
 * that shows them).
 *
 * B10: `FR-PERM-018` (add a member to a board by email or username).
 * `FR-PERM-029` (remove one) and `FR-PERM-032` (last-admin protection) land no
 * shape here — removal is `DELETE /boards/:id/members/:userId`, which answers
 * `204` with no body and therefore needs no response schema, and last-admin
 * protection is a `409` refusal on routes this file already describes
 * (`boardRolePatchSchema`, and now the removal route), not a body of its own.
 *
 * B38 touches this file in **exactly three places**, and every one of them is a
 * prediction an earlier block wrote down coming true rather than a new subject
 * arriving: `boardRolePatchSchema` admits `'observer'` now that `BE-38.1` has
 * built `FR-PERM-016`'s exclusions, `addBoardMemberBodySchema` accepts the
 * `role` choice `FR-PERM-018`'s own sentence offers, and `boardMemberSchema`
 * gains `isGuest` for `FR-PERM-023`'s edge case. **Each of the three rewrites
 * the argument for its own absence in place rather than deleting it** — the
 * arguments were right, they named their own conditions, and the conditions
 * have been met. B38's *new* subjects are three files of their own
 * (`./board-permissions`, `./board-invite-link`, `./workspace-join-request`)
 * and not one field of them is here, because this file is a board's identity
 * and its member list, not everything a board has a setting for.
 *
 * Narrow on purpose, exactly as `./user` is B06's subset and `./workspace` is
 * B07's. **`BE-02.1` — the full Phase 1 object model (board, list, card,
 * comment, label, notification) — is a different item and stays PENDING**, and
 * this file is not its first instalment. What is here is what B08's, B09's and
 * B10's routes actually accept and return: a board's identity, its title, its
 * address, whether it is closed, who may see it and who is on it. A board's
 * *contents* are `BE-13.1`; a list is B11; a card is `BE-12.1`. The card shapes
 * at the foot of this file are addressing facts only, for the resolver
 * `FR-EXP-015` requires, and carry not one field of card content.
 *
 * ⚠ **All three have landed and this file is unchanged by them, which is the
 * division working rather than an omission.** `boardContentsSchema` in
 * `./board-contents` (B13) nests `boardSchema` **as it is** beside an array of
 * lists carrying their cards, so a board's contents compose this schema instead
 * of widening it — and `GET /boards/:id` still answers with a board's header
 * facts and nothing beneath them.
 *
 * The file follows the same division `./user` and `./workspace` follow, for the
 * same reason: **input primitives**, which validate and normalise what a route
 * accepts, and **views**, which describe what a route returns. An input
 * primitive reused as a response field is the mistake the division prevents — a
 * response schema that trims returns a title the database does not hold, and one
 * that rejects refuses to serve a row that legitimately exists.
 *
 * ---------------------------------------------------------------------------
 * A CONTRADICTION IN THE CORPUS, RESOLVED HERE ONCE
 * ---------------------------------------------------------------------------
 * `FR-PERM-004`'s behaviour text says "Boards may also exist outside any
 * workspace, but such boards get no workspace-visible option." `FR-BOARD-001`
 * says "a board cannot exist outside a workspace". They cannot both hold, and
 * two things settle it the same way: the authority order (behaviour is owned by
 * the domain document — `01-boards-lists-cards.md` owns boards, `06-` does not),
 * and `boards.workspace_id char(26) NOT NULL`, which makes the workspace-less
 * board inexpressible. So `createBoardBodySchema` requires a `workspaceId` and
 * there is no branch without one. If the workspace-less board is ever wanted it
 * is a migration and a requirement, not an optional field here.
 *
 * ---------------------------------------------------------------------------
 * WHAT IS DELIBERATELY ABSENT, so that its absence reads as a decision
 * ---------------------------------------------------------------------------
 *   - ~~**`visibility` on the *creation* body.**~~ **BUILT BY `BE-09.4`, and
 *     this entry is rewritten rather than removed because what stood here was
 *     the defect two later items were opened to record.** It read that
 *     `createBoardBodySchema` "rejects the key rather than stripping it, so a
 *     board starts at the column default", called that "a recorded gap, not a
 *     decision", and sat under a heading that says the opposite — WHAT IS
 *     DELIBERATELY ABSENT. Four other files copied the sentence, and
 *     `BE-67.12` and `BE-67.16` both exist because a gap filed under
 *     "deliberate" stops being looked at. The create body now carries an
 *     **optional** `visibility`, which is `FR-BOARD-001`'s own Behaviour
 *     sentence — *"New boards default to workspace-visible …, with private and
 *     public alternatives selectable before creation"* — and the optionality is
 *     AC3: *"given the user has not changed the visibility control"* is an
 *     omitted key, and the answer to an omitted key is resolved by the server
 *     (`enterprise_settings.default_board_visibility` where the workspace is
 *     Enterprise-linked, `'workspace_visible'` otherwise) rather than by this
 *     schema. The **full four-value** `boardVisibilitySchema` is accepted here
 *     and not a narrowed copy: `enterprise_visible` is a value the column can
 *     hold, and whether this workspace may hold it is a state question the route
 *     answers with 409 (`FR-PERM-011` AC1), never a parse failure. There is
 *     deliberately **no `confirmWorldReadable`** companion — see
 *     `createBoardBodySchema` itself, which argues it.
 *   - **The caller's own capabilities on the board** — a `myRole`, a `canClose`,
 *     a `canEdit`. `FR-BOARD-005`'s edge case ("entries the current member lacks
 *     permission for are hidden, not shown-and-erroring") means `FE-08.2` needs
 *     one of these to render the board menu, and it is still not this file's to
 *     invent even now that board roles cross the wire. `boardMemberSchema` says
 *     *what role a listed member holds*; a capability field would say *what this
 *     caller may do*, which is a different question computed from a model that
 *     is still half-built: observer exclusions are `FR-PERM-016`/`BE-38.1`, the
 *     workspace-admin cascade over board capability is `FR-PERM-017`/`BE-39.3`,
 *     and per-board permission settings are `FR-PERM-026`–`FR-PERM-028`
 *     /`BE-38.5`. A capability field shipped now would have to change meaning
 *     three more times. ⚠ **Two of those three landed at B38** — `BE-38.1`'s
 *     observer exclusions and `BE-38.5`'s per-board settings (the latter in
 *     `./board-permissions`, a file of its own) — **and the field is still
 *     absent, because the third has not.** `FR-PERM-017`'s workspace-admin
 *     cascade over board capability is `BE-39.3`'s and Phase 2, so a capability
 *     field shipped today would still have to change meaning once more. Two of
 *     three is not the condition this bullet named.
 *   - **Removing a board member, and last-admin protection.** `FR-PERM-029` is
 *     `DELETE /boards/:id/members/:userId`, which is bodiless by construction
 *     (the path names the whole request) and answers `204` on success, so it
 *     needs no schema in this file — only a route. `FR-PERM-032`'s refusal
 *     ("the reason names the single-admin rule") is a `409 conflict` on that
 *     route and on `boardRolePatchSchema`'s, not a shape either: it is a guard
 *     the server enforces, and a Zod schema cannot express "the database
 *     currently holds exactly one admin". **Adding a member is no longer
 *     absent** — see `addBoardMemberBodySchema` and `addBoardMemberResultSchema`
 *     below, B10, `FR-PERM-018`.
 *   - **`description`, the background fields, `isTemplate`, the card-number
 *     board setting and the other twenty-odd columns on `boards`.** The board
 *     description is `FR-BOARD-006`; the background is `FR-BOARD-002` (V1);
 *     `boards.show_card_numbers_on_front` is `FR-CARD-005`'s optional board
 *     setting and B08 does not build the toggle. Each is a real column with no
 *     B08 route behind it, and a field on the wire is a field the web client may
 *     bind to and a later block may not remove (`./workspace`'s "No `createdAt`"
 *     note is the precedent).
 *   - **Starring** (`FR-BOARD-004`, V1) — per-member, so it is not a property of
 *     the board at all and would need a viewer-dependent field.
 *   - **Card content of any kind.** `BE-12.1` owns the card contract. `title`
 *     appears in `cardAddressSchema` only because the slug is derived from it
 *     and `FR-EXP-015` AC1 requires the caller to be able to correct its address
 *     bar after a rename.
 *
 * Every width constant below is a real column width from
 * `db/postgres/migrations/0002_boards_lists_cards.sql`, stated next to the
 * column it comes from. Getting one wrong is not a type error — it is
 * `value too long for type character varying(n)` at INSERT time, surfaced as a
 * generic failure, which is the class of mistake this package exists to catch at
 * the boundary.
 */

// ---------------------------------------------------------------------------
// Column widths. One place to change when a migration widens a column.
// ---------------------------------------------------------------------------

/** `boards.title varchar(512)`. */
export const BOARD_TITLE_MAX_LENGTH = 512;

/**
 * `cards.title varchar(512)`.
 *
 * Here rather than in a card contract file because `cardAddressSchema` below is
 * the only shape in this package that carries a card title today. `BE-12.1`
 * should **import** this constant when it writes the card contract, not restate
 * a second 512 that can then drift from this one.
 */
export const CARD_TITLE_MAX_LENGTH = 512;

/**
 * `boards.short_link varchar(16) COLLATE "C"` and
 * `cards.short_link varchar(16) COLLATE "C"` — both `NOT NULL` and both uniquely
 * indexed (`uq_boards_short_link`, `uq_cards_short_link`).
 *
 * This is the **column** width, not the length of the token the server
 * generates. `FR-EXP-015`'s edge case ("short links must not be guessable in a
 * way that reveals content") is an entropy requirement on the generator, and the
 * generator owns that number and the collision arithmetic behind it — the same
 * division `./workspace` uses, where `WORKSPACE_SHORT_NAME_MAX_LENGTH` lives
 * here and the derivation lives with its only writer.
 *
 * The `COLLATE "C"` is why `shortLinkSchema` below does not fold case: the
 * column is compared byte-wise, so `aB3` and `Ab3` are two different rows and
 * folding here would make a lookup find the wrong one — or none.
 */
export const SHORT_LINK_MAX_LENGTH = 16;

// ---------------------------------------------------------------------------
// Vocabularies — B09.
//
// Both closed sets below are stated **once** here and mirror a CHECK
// constraint in `db/postgres/migrations/0002_boards_lists_cards.sql`. A value
// outside one of them is not a compile error at the call site that writes it —
// it is a runtime CHECK violation on INSERT, exactly as an unregistered event
// type is (`./events` says the same of `ck_activity_entries_type`).
//
// Both are also the place this file's input/view division shows up as a
// *narrowing* rather than as two separate shapes, and it is the same rule
// twice: **the view admits every value the column admits, and the request body
// admits only the values this block can actually set.** A view that refused a
// legal row would be a 500 on a read for data the database considers valid; a
// request that accepted a value no route implements would be a silent no-op the
// caller reads as success.
//
// ⚠ **The worked example this paragraph used to give was "visibility is four
// out and three in, the board role is three out and two in".** Visibility is
// now four out and four in because `BE-67.1` made `enterprise_visible`
// writable with its read arm and policy checks beside it. The board role is
// now **three out and three in
// on the role patch**, because `BE-38.1` built the observer exclusions and
// `boardRolePatchSchema` can honour the value it used to refuse; it is **three
// out and two in on the two routes that add somebody who is not on the board
// yet** (`GRANTABLE_BOARD_ROLES`), where the excluded value is `'admin'` and
// the reason is `FR-PERM-015` AC3 rather than an unbuilt block. So the file now
// carries *both* directions of the same rule at once, which is the clearest
// statement of it this file has ever had: a narrowing that names an unbuilt
// block disappears when that block lands, and a narrowing that names a
// requirement does not.
// ---------------------------------------------------------------------------

/**
 * `ck_boards_visibility CHECK (visibility IN ('private', 'workspace_visible',
 * 'enterprise_visible', 'public'))`, `boards.visibility varchar(32) NOT NULL
 * DEFAULT 'workspace_visible'`.
 *
 * **Four values, and `enterprise_visible` is in the list on purpose.** B67 made
 * that level writable and readable by Enterprise workspace members; it remains
 * listed here because this tuple is the *view* vocabulary and the column can
 * hold the value. Every read path that parses `boards.visibility` through this
 * schema must serve legal stored values rather than throw. The
 * house rule is that a value read out of a `varchar` with a CHECK is *parsed*,
 * never cast, and a parser narrower than its CHECK turns a legal row into a 500.
 *
 * Note for whoever composes the read predicate: write the visibility arms as
 * **equality** (`= 'workspace_visible'`, `= 'public'`) and never as a negation
 * such as `<> 'private'`. A negation silently hands `enterprise_visible` the
 * wrong audience; the Enterprise arm has its own workspace-link and membership
 * checks.
 */
export const BOARD_VISIBILITIES = [
  'private',
  'workspace_visible',
  'enterprise_visible',
  'public',
] as const;

export const boardVisibilitySchema = z.enum(BOARD_VISIBILITIES);

export type BoardVisibility = z.infer<typeof boardVisibilitySchema>;

/**
 * `ck_board_members_role CHECK (role IN ('admin','normal','observer'))`.
 *
 * **Moved here from `apps/server/src/boards/permissions.ts` by B09**, and the
 * reasoning it was declared under there is carried across rather than dropped,
 * because it was right and its condition has simply been met. That comment said:
 * a workspace role crosses the wire — `workspaceMemberSchema` carries one and
 * `PATCH …/members/:userId/role` accepts one — whereas no B08 response carried a
 * board role and no B08 request accepted one, and a shape that never crosses the
 * wire does not belong in the contract package; it should move "exactly when a
 * route first returns or accepts a board role".
 *
 * That moment is B09, one block earlier than the note guessed (it named
 * `BE-10.1`–`BE-10.3`): `GET /boards/:id/members` returns a board role and
 * `PATCH /boards/:id/members/:userId/role` accepts one, both under `BE-09.3` for
 * `FR-PERM-015`. So it moved, and the server module re-exports these names so
 * that no existing call site is touched by the move.
 *
 * This is **not** the workspace role set (`./workspace`'s `WORKSPACE_ROLES`),
 * and the two must not be merged. `ck_workspace_members_role` admits two values;
 * this admits three. `WORKSPACE_ROLES`'s own docstring already records that they
 * are deliberately separate vocabularies applying simultaneously — the
 * capability matrix above `FR-PERM-014` is the reason — and merging them would
 * mean widening one of the two CHECK constraints to make a shared type check.
 *
 * ⚠ **`'observer'` (`FR-PERM-016`) WAS a real, storable role that nothing
 * enforced, and `BE-38.1` HAS LANDED — this paragraph is corrected in place
 * rather than deleted, because it was right and the condition it named has been
 * met.** What it said through B37: "the permission module parses it,
 * `requireBoardReader` treats an observer as a reader, which is right, and no
 * route refuses an observer a write, which is wrong and is `BE-38.1`'s to
 * correct. It is in this tuple because a row can hold it; it is absent from
 * `boardRolePatchSchema` below because B09 cannot honour it."
 *
 * B38 corrected it in `requireBoardWriter` (`apps/server/src/boards/
 * permissions.ts`) — **one guard, and deliberately only one**, because every
 * other content guard in the server delegates to it (`requireCardWriter`,
 * `requireListWriter`, the two checklist sites and the attachment site). So an
 * observer now reads a board and is refused every write on it, with the single
 * exception `FR-PERM-016` states itself: an observer who is separately a
 * **workspace admin** keeps the wider workspace-admin authority, because the
 * more permissive role wins. `boardRolePatchSchema` below therefore admits all
 * three values now, and `addBoardMemberBodySchema` admits two of them.
 */
export const BOARD_ROLES = ['admin', 'normal', 'observer'] as const;

export const boardRoleSchema = z.enum(BOARD_ROLES);

export type BoardRole = z.infer<typeof boardRoleSchema>;

/**
 * The board roles a route may **grant to somebody who is not on the board yet**
 * — `FR-PERM-018`, "New members are added at normal level unless the inviter
 * chooses observer".
 *
 * ⚠ **Two values, and the missing one is `'admin'`, on two independent
 * grounds** — stated once here because this tuple has two consumers
 * (`addBoardMemberBodySchema` below and `./board-invite-link`'s creation body)
 * and a second declaration would let them drift into disagreeing about who may
 * mint an admin:
 *
 *   1. **Promotion is `PATCH /boards/:id/members/:userId/role`'s power and
 *      `FR-PERM-015` AC3 keeps it there** — "admins can change any board
 *      member's role". Admitting `'admin'` on an *add* route would make the
 *      role patch's last-admin guard (`FR-PERM-032`) reachable around rather
 *      than through, because a caller could manufacture a second admin without
 *      ever addressing a member row.
 *   2. **On an invite link the consequence is sharper still.** A link is a
 *      credential anybody holding the URL may redeem (`FR-PERM-019`), so an
 *      admin-granting link is a URL that hands board administration to whoever
 *      forwards it furthest. `ck_board_invite_links_role_granted` admits
 *      `'admin'` at the column — `0002` wrote all three — which is exactly why
 *      the refusal has to be stated in the contract rather than assumed from
 *      the schema.
 *
 * The **view** side is `boardRoleSchema` (all three) wherever a stored role is
 * served, on `BOARD_VISIBILITIES`' rule: the view admits every value the column
 * admits, the request body admits only the values a route may actually set.
 */
export const GRANTABLE_BOARD_ROLES = ['normal', 'observer'] as const;

export const grantableBoardRoleSchema = z.enum(GRANTABLE_BOARD_ROLES);

export type GrantableBoardRole = z.infer<typeof grantableBoardRoleSchema>;

// ---------------------------------------------------------------------------
// INPUT primitives — what a route ACCEPTS.
// ---------------------------------------------------------------------------

/**
 * A board title as `boards.title` stores it.
 *
 * Trimmed before it is measured, the `workspaceNameSchema` pattern and for the
 * same stated reason: trailing whitespace must not smuggle a title past the
 * width bound, and a title made only of spaces would otherwise become a blank
 * entry in every member's board list and a blank browser tab title
 * (`FR-BOARD-003` AC1 names all three surfaces).
 *
 * `.min(1)` after the trim is the requirement, twice over and not a house
 * preference: `FR-BOARD-001` AC1 — "given the create-board form with an empty
 * title, creation is blocked" — and `FR-BOARD-003` AC3 — "given an empty title,
 * when the user commits, then the rename is rejected and the previous title is
 * kept". One primitive serves both, so the two can never disagree about what
 * "empty" means.
 */
export const boardTitleSchema = z.string().trim().min(1).max(BOARD_TITLE_MAX_LENGTH);

/**
 * The alphabet a short link is drawn from — base 62, and nothing else.
 *
 * Both short-link columns are `COLLATE "C"`, so the comparison is byte-wise and
 * case-sensitive; a value outside this alphabet cannot have been produced by the
 * generator, so a path param that fails this regex is a 422 before any row is
 * read rather than a guaranteed-miss `SELECT`. That is not an existence oracle:
 * the answer depends only on the shape of the string the caller sent, exactly as
 * `ulidSchema` on a `:id` param does (`routes/workspaces.ts` states the rule).
 */
const SHORT_LINK_PATTERN = /^[0-9A-Za-z]+$/;

/**
 * A short link as either column holds it — the value in `/b/<shortLink>/…` and
 * `/c/<shortLink>/…`.
 *
 * **Case is not folded.** `ulidSchema` says why in the same words: under a
 * deterministic collation Postgres compares text case-sensitively, and folding
 * here is how two distinct rows become one. Unlike an email address — which
 * Better Auth lower-cases on every write, so folding a lookup *helps* — a short
 * link is a random mixed-case token and the case is part of the value.
 */
export const shortLinkSchema = z
  .string()
  .min(1)
  .max(SHORT_LINK_MAX_LENGTH)
  .regex(SHORT_LINK_PATTERN, 'must be a base-62 short link');

/**
 * Longest a card reference may be on the wire: a ten-character key, the
 * hyphen, and a number with room for leading zeros. Generous on purpose — a
 * reference pasted as `MG-00001` must not 422 — but bounded, because the value
 * is matched by a regular expression before anything else.
 */
const CARD_REF_PARAM_MAX_LENGTH = BOARD_CARD_KEY_MAX_LENGTH + 1 + 20;

/**
 * `GET /cards/by-short-link/:shortLink`'s param — a short link **or** a card
 * reference (`MG-0001`), `docs/notes/card-keys.md`, "Resolution".
 *
 * ⚠ **A copy, never `shortLinkSchema` widened in place.** The board resolver
 * and every other route on `shortLinkSchema` must keep rejecting a hyphen; a
 * reference names a card, and only this route resolves one. The two arms
 * cannot overlap: a short link is base 62 and a reference requires the hyphen,
 * so `MG0001` is a short link and `MG-0001` is a reference — the route tells
 * them apart with `parseCardRef`, never by length or case.
 */
export const cardShortLinkOrRefSchema = z
  .string()
  .min(1)
  .max(CARD_REF_PARAM_MAX_LENGTH)
  .refine(
    (value) =>
      (value.length <= SHORT_LINK_MAX_LENGTH && SHORT_LINK_PATTERN.test(value)) ||
      parseCardRef(value) !== null,
    'must be a base-62 short link or a card reference such as MG-0001',
  );

/**
 * A board-scoped card number — `FR-CARD-005`, `cards.card_number integer`.
 *
 * Positive, so the allocator's contract is stated once: numbering starts at 1
 * and `FR-CARD-005` AC3 requires the later of two cards to hold the higher
 * number. `cards.card_number` is *nullable* in the schema (a card in a personal
 * inbox has neither a board nor a number), which is a state
 * `cardAddressSchema` deliberately cannot express — see its docstring.
 *
 * Exported for the allocator, which parses rather than casts what it reads back
 * from `MAX(card_number) + 1`.
 */
export const cardNumberSchema = z.number().int().positive();

/**
 * A card reference **as a projection emits it** — `formatCardRef`'s output in
 * `./address`: an upper-case key, the hyphen, and the number padded to four
 * digits and never truncated (`MG-0001`, `MG-10000`). `docs/notes/card-keys.md`,
 * "Wire".
 *
 * Narrower than `cardShortLinkOrRefSchema`, which admits what a person types
 * (`mg-1`); this is what the server writes. Every card projection carries it as
 * **`ref: cardReferenceSchema.nullable()`, required** — `null` for an inbox
 * card and for a viewer outside the reference scope (`isBoardReferenceableBy`),
 * exactly when the projection's `path` carries the short link instead. Never
 * `.optional()`: a mapper that forgets the field must fail to compile.
 */
export const cardReferenceSchema = z
  .string()
  .max(CARD_REF_PARAM_MAX_LENGTH)
  .regex(/^[A-Z][A-Z0-9]{1,9}-[0-9]{4,10}$/, 'must be a card reference such as MG-0001');

/**
 * `POST /boards` — `FR-BOARD-001`, "Board creation asks for a title, a
 * destination workspace and a background in a single step".
 *
 * All three, as of B42 — and the *fourth* thing the Behaviour sentence asks for
 * as of `BE-09.4`: *"New boards default to **workspace-visible** …, with private
 * and public alternatives selectable before creation."*
 *
 * A `strictObject`, like every body in `./user` and `./workspace`: a plain
 * `z.object()` in Zod 4 **strips** unknown keys rather than rejecting them, and
 * here that would be a security-shaped failure rather than an inconvenience. A
 * stripped `slug` or `closed` is an inconvenience; a stripped *anything the
 * caller believes changes who can read the board* is a disclosure. The strict
 * form makes an undeclared key a 422 naming it.
 *
 * ---------------------------------------------------------------------------
 * `visibility` IS OPTIONAL, AND THE OPTIONALITY IS AC3 ITSELF
 * ---------------------------------------------------------------------------
 * ⚠ **This paragraph replaces one that said the key was refused on purpose, and
 * that sentence was copied into four other files before anybody noticed it was
 * describing an unbuilt clause rather than a decision** (`BE-67.12`,
 * `BE-67.16`). Omitting the key is `FR-BOARD-001` AC3's antecedent word for
 * word — *"given the user has not changed the visibility control"* — so the
 * default is expressed by ABSENCE and never by a client-side literal. A client
 * that sent the level it merely rendered would freeze today's answer into every
 * request and quietly defeat `FR-PERM-033` AC4, whose whole content is that the
 * server picks a different default for an Enterprise-linked workspace. The
 * server resolves the omitted case: the Enterprise default where there is a
 * linkage, `'workspace_visible'` otherwise.
 *
 * **All four levels, not the three the *patch* body accepts before `BE-67.1`
 * widened it, and not a narrowed copy.** `enterprise_visible` parses here
 * because the column can hold it; whether *this* workspace may is a state
 * question, and `POST /boards` answers it with 409 on `FR-PERM-011` AC1 rather
 * than with a parse failure. A parser narrower than the column turns "your
 * workspace is not linked to an Enterprise" into "that is not a visibility",
 * which is a different and less useful sentence.
 *
 * **No `confirmWorldReadable` companion, and that is a ruling.**
 * `boardVisibilityPatchSchema` requires that literal on its public arm because
 * `FR-PERM-010` constrains *"**switching** a board to public"*. Creation is not
 * switching: no board exists, no audience widens, and nobody's expectation about
 * an existing board is being overturned. Requiring it here would also put a
 * second confirmation inside the create dialog for a board that does not exist
 * yet. The switch path keeps its confirmation untouched.
 *
 * ---------------------------------------------------------------------------
 * ⚠ WIDENING THIS ALSO WIDENED THE OFFLINE REPLAY CONTRACT, AND THAT IS
 * DELIBERATE RATHER THAN INCIDENTAL
 * ---------------------------------------------------------------------------
 * This is not the body of one route. `createBoardOperationSchema` in
 * `./offline-sync` uses it verbatim as its `body`, and the server replays a
 * queued `board.create` through the SAME `createBoard` call, so a queued
 * operation can now carry a chosen level — which is right, since a board
 * composed offline should arrive at the level the person picked rather than at
 * whatever the default was when the queue drained. **The consequence to know
 * is the failure set:** a replayed create could previously only come back 404
 * or 422, and can now also come back 403 (Enterprise policy) or 409
 * (`enterprise_visible` on an unlinked workspace) — both of them states that
 * may have changed while the client was offline, which is exactly the case
 * offline replay exists to handle. It was recorded here rather than "fixed" by
 * forking a narrower copy for the queue: two create contracts that disagree
 * about what a board may be is a worse failure than a replay that can be
 * refused.
 *
 * The `.omit()` in `apps/mcp/src/tools/create-board.ts` is the other direction
 * of the same fact, and it had to be widened in step: §2.4 keeps `visibility`
 * off the connected-app tool surface, and that exclusion used to come free from
 * this schema's silence.
 *
 * `background` uses B42's board-background write schema rather than widening
 * this route to a whole-board creation object. Valid colour/photo values are
 * stored at creation time; malformed values and unsupported custom uploads are
 * still rejected rather than stripped.
 *
 * Everything else takes its column default — `commenting_permission 'members'`,
 * `member_add_remove_permission 'all_members'`. Those are settable only through
 * their own field-scoped patches (SC-4), and deliberately not from here.
 */
export const createBoardBodySchema = z.strictObject({
  workspaceId: ulidSchema,
  title: boardTitleSchema,
  visibility: boardVisibilitySchema
    .optional()
    .describe(
      'Who can find and open the new board (FR-BOARD-001). Omit it to take the ' +
        'default, which is workspace-visible unless the workspace belongs to an ' +
        'Enterprise that sets another one; omitting is not the same as sending ' +
        'workspace_visible, because only the omitted form lets that policy ' +
        'apply. private restricts the board to the people added to it; public ' +
        'makes it readable by anyone on the internet, signed in or not, and ' +
        'needs no separate confirmation at creation because no existing ' +
        'audience is being widened. enterprise_visible is accepted only where ' +
        'the workspace is linked to an Enterprise and that Enterprise’s policy ' +
        'allows the level, and is refused rather than quietly narrowed.',
    ),
  background: boardBackgroundPutSchema.optional(),
});

export type CreateBoardBody = z.infer<typeof createBoardBodySchema>;

/**
 * `PATCH /boards/:id/title` — `FR-BOARD-003`. SC-4, field-scoped.
 *
 * The title moves on its own and can move nothing else. **Never a
 * `PUT /boards/:id`** carrying the whole board: `boards` has thirty-odd columns
 * owned by eight later blocks, an absent key and a cleared value are the same
 * thing in the body a PUT receives, and `FR-EXP-031`'s per-field last-write-wins
 * cannot be expressed against it. `FR-COLLAB-026` is the sharper case — two
 * members editing a board at once must not clobber each other's field — and a
 * whole-object write makes that impossible by construction. The same rule the
 * profile patches and the workspace role patch follow (`apps/server/README.md`
 * §3).
 */
export const boardTitlePatchSchema = z.strictObject({
  title: boardTitleSchema,
});

export type BoardTitlePatch = z.infer<typeof boardTitlePatchSchema>;

/**
 * `PATCH /boards/:id/visibility` — `FR-PERM-012`. SC-4, field-scoped, and the
 * same "never a `PUT /boards/:id`" argument `boardTitlePatchSchema` gives.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS A DISCRIMINATED UNION AND NOT `{ visibility: boardVisibilitySchema }`
 * ---------------------------------------------------------------------------
 * `FR-PERM-010` does not merely permit a public board, it constrains how one is
 * made: switching a board to public "must require an explicit confirmation
 * stating that the board becomes world-readable". A plain `{ visibility }` body
 * would leave that confirmation entirely to the client — the dialog would be the
 * only thing standing between a member and a world-readable board, and a caller
 * that never renders the dialog (a script, a second client, a stale build, a
 * mistaken `curl`) would satisfy the contract while defeating the requirement.
 *
 * That alternative was considered and rejected on **B08's own precedent, set in
 * this same package's neighbourhood**: `FR-BOARD-008`'s staged destruction
 * (close before delete) is enforced server-side with a 409 rather than by hiding
 * a menu entry, under the recorded reasoning that *a client that hides the menu
 * entry is not an enforcement of it*. The identical argument applies here and
 * with more at stake, because the failure is disclosure rather than an
 * unexpected deletion.
 *
 * So the confirmation is a **required key on exactly one arm**. Sending
 * `{ visibility: 'public' }` is a 422 naming `confirmWorldReadable`; sending
 * `confirmWorldReadable: false` is a 422 too, because the field is
 * `z.literal(true)` and not `z.boolean()` — a caller that has understood the
 * consequence says so, and "I have read this and decline" is not a thing this
 * route can act on. Narrowing to `private` or `workspace_visible` needs no
 * confirmation: `FR-PERM-012` AC3 makes narrowing take effect immediately and
 * nothing is disclosed by it. Each arm is a `strictObject`, so
 * `{ visibility: 'private', confirmWorldReadable: true }` is also a 422 rather
 * than a confirmation quietly accepted for a change that did not need one.
 *
 * `enterprise_visible` is a member of this union as of `BE-67.1`; the server
 * still enforces the Enterprise-linked workspace and policy requirements before
 * storing it.
 *
 * ---------------------------------------------------------------------------
 * THE `.describe()` TEXT IS THE PRODUCT COPY'S SOURCE, NOT DECORATION
 * ---------------------------------------------------------------------------
 * These strings reach the OpenAPI document, which is what `FE-09.1` builds its
 * visibility picker and its confirmation dialog against. Two things are
 * deliberate in the wording and must survive an edit:
 *
 *   - **Attachments and card covers are named.** `FR-PERM-010`'s edge case says
 *     they are reachable by anyone holding the board URL and that "the
 *     confirmation copy should say so". If this sentence does not name them,
 *     `FE-09.1` has to invent one.
 *   - **Nothing here says "published", "listed" or "discoverable".** A public
 *     board is reachable by link and is not advertised: `FR-PERM-010`'s other
 *     edge case says search-engine indexing is *not* implied by public
 *     visibility, `06-`'s Open questions place indexing at the workspace level
 *     only, and B09 records the same answer for the product's own search
 *     (OQ-8 — a public board is not returned in the search results of a user who
 *     is not a member of it; `BE-20.1` consumes that).
 */
export const boardVisibilityPatchSchema = z.discriminatedUnion('visibility', [
  z
    .strictObject({ visibility: z.literal('private') })
    .describe(
      'Private — only people on the board member list can find or open it. ' +
        'Workspace members who have not been added lose sight of it, including ' +
        'in the workspace board list; workspace admins keep access ' +
        '(FR-PERM-008). Anyone already on the member list keeps access.',
    ),
  z
    .strictObject({ visibility: z.literal('workspace_visible') })
    .describe(
      'Workspace-visible — every member of the board’s workspace can open the ' +
        'board without being invited to it (FR-PERM-009). Opening it does not ' +
        'add them to the board member list.',
    ),
  z
    .strictObject({ visibility: z.literal('enterprise_visible') })
    .describe(
      'Enterprise-visible — every member of at least one workspace in the same ' +
        'Enterprise can open the board (FR-PERM-011). Only available for boards ' +
        'whose workspace is linked to an Enterprise and when workspace policy ' +
        'allows this visibility.',
    ),
  z
    .strictObject({
      visibility: z.literal('public'),
      confirmWorldReadable: z
        .literal(true, {
          /**
           * THE MESSAGE NAMES THE KEY, AND THAT IS THE REQUIREMENT RATHER THAN
           * A COURTESY.
           *
           * Zod's own text for a failed `z.literal(true)` is "Invalid input:
           * expected true", which names the *value* and never the *field*. The
           * field is carried on the issue's `path`, but `app.ts`'s error
           * handler maps only `issue.message` into `details` — one convention
           * for every 422 in the product — so an unmodified message reaches the
           * caller as a refusal that does not say what was omitted.
           *
           * `FE-09.1` has to tell "you must confirm" apart from "your body was
           * malformed" in order to re-open the confirmation rather than show a
           * generic error, and telling them apart is the entire reason this
           * body is a discriminated union with a required literal instead of a
           * plain `{ visibility }` object. Overriding the message here rather
           * than folding `instancePath` into the error handler keeps the change
           * scoped to the one field that needs it: the handler's shape is
           * shared by every route in the server, and widening it to satisfy
           * this route would alter the body of every validation failure in the
           * product for a reason none of them asked for.
           */
          error:
            'confirmWorldReadable must be true to make a board public: it ' +
            'becomes readable by anyone on the internet, signed in or not.',
        })
        .describe(
          'Required acknowledgement that the board becomes world-readable. ' +
            'Anyone holding the link can read this board without signing in — ' +
            'its lists, its cards, its comments, and its attachments and card ' +
            'covers. Editing stays restricted to board members: visibility ' +
            'never grants write access. The board is reachable by link and is ' +
            'not advertised anywhere, and search-engine indexing is not implied ' +
            '(FR-PERM-010). Send true to confirm; the request is refused ' +
            'without it.',
        ),
    })
    .describe(
      'Public — readable by anyone on the internet, signed in or not ' +
        '(FR-PERM-010). Requires confirmWorldReadable: true.',
    ),
]);

export type BoardVisibilityPatch = z.infer<typeof boardVisibilityPatchSchema>;

/**
 * A board key as a client may send it — `docs/notes/card-keys.md`, "Changing a
 * key". Trimmed, checked against the grammar **case-insensitively**, then
 * upper-cased, so `mg` is `MG`: a key is typed by hand and the stored form is
 * upper-case only (`ck_boards_card_key`). Anything else — a leading digit, a
 * hyphen, eleven characters — is a 422 from the grammar before any row is read.
 *
 * ⚠ The grammar runs **before** the upper-casing, on ASCII only. The other way
 * round, JavaScript's full Unicode case mapping would turn `maß` into `MASS`
 * and `mı` into `MI`, both of which pass, and store a key nobody typed.
 */
export const boardCardKeyInputSchema = z
  .string()
  .trim()
  .regex(
    new RegExp(BOARD_CARD_KEY_PATTERN.source, 'i'),
    'must be 2-10 letters or digits, starting with a letter (e.g. MG)',
  )
  .toUpperCase();

/**
 * `PATCH /boards/:id/key` — change the key the board's card references use
 * (`MG-0001`). Field-scoped per SC-4; the response is the board
 * (`boardSchema`, which carries `cardKey`), the shape `PATCH …/visibility`
 * returns.
 */
export const boardCardKeyPatchSchema = z.strictObject({
  key: boardCardKeyInputSchema,
});

export type BoardCardKeyPatch = z.infer<typeof boardCardKeyPatchSchema>;

/**
 * `boards.show_card_numbers_on_front` — `FR-CARD-005`'s optional board setting
 * ("with the board setting to show card numbers enabled, each card front
 * displays its card number"), as `PATCH /boards/:id/card-numbers-on-front`
 * answers it (`docs/notes/card-keys.md`). The column has existed since
 * `0002_boards_lists_cards.sql` (`boolean NOT NULL DEFAULT false`); this is the
 * first route to write it.
 *
 * `boardCoverSettingsSchema`'s exact shape and for its reasons: a view, so
 * `z.object`; the board it answers about, so a client holding several facet
 * responses can tell them apart; and required and non-nullable, because the
 * column is `NOT NULL` and has no third state.
 *
 * ⚠ What the front renders is the card's `ref` (`MG-0001`), not the bare
 * number: the reference is what people paste, and a viewer outside the
 * reference scope gets `ref: null` and sees nothing — the number alone would
 * be a second, unlinkable name for the same card.
 */
export const boardCardNumbersSettingSchema = z.object({
  boardId: ulidSchema,
  showCardNumbersOnFront: z.boolean(),
});

export type BoardCardNumbersSetting = z.infer<typeof boardCardNumbersSettingSchema>;

/**
 * The body of `PATCH /boards/:id/card-numbers-on-front` — SC-4's field-scoped
 * patch, strict and with its one field required, `boardCoverSettingsPatchSchema`'s
 * call: `{}` asks for nothing and is a 422 decided by the schema.
 */
export const boardCardNumbersSettingPatchSchema = z.strictObject({
  showCardNumbersOnFront: z.boolean(),
});

export type BoardCardNumbersSettingPatch = z.infer<
  typeof boardCardNumbersSettingPatchSchema
>;

/**
 * `boards.required_approvals` (`ck_boards_required_approvals`, 1..20) — how many
 * approvals a PR-list card needs to count as met. Answer of
 * `PATCH /boards/:id/required-approvals`; the board face reads the same value
 * on `boardContentsBoardSchema.requiredApprovals`.
 */
export const REQUIRED_APPROVALS_MIN = 1;
export const REQUIRED_APPROVALS_MAX = 20;

export const boardRequiredApprovalsSchema = z.object({
  boardId: ulidSchema,
  requiredApprovals: z
    .number()
    .int()
    .min(REQUIRED_APPROVALS_MIN)
    .max(REQUIRED_APPROVALS_MAX),
});

export type BoardRequiredApprovals = z.infer<typeof boardRequiredApprovalsSchema>;

/** The body of `PATCH /boards/:id/required-approvals` — field-scoped, strict. */
export const boardRequiredApprovalsPatchSchema = z.strictObject({
  requiredApprovals: z
    .number()
    .int()
    .min(REQUIRED_APPROVALS_MIN)
    .max(REQUIRED_APPROVALS_MAX),
});

export type BoardRequiredApprovalsPatch = z.infer<
  typeof boardRequiredApprovalsPatchSchema
>;

/**
 * `boards.dangerous_comments` (`ck_boards_dangerous_comments`, NULL or 1..10000)
 * — the comment count at which the card front takes a reddish background.
 * `null` = off. Owner-directed, no requirement. Answer of
 * `PATCH /boards/:id/dangerous-comments`; the board face reads the same value on
 * `boardContentsBoardSchema.dangerousComments`.
 */
export const DANGEROUS_COMMENTS_MIN = 1;
export const DANGEROUS_COMMENTS_MAX = 10000;

const dangerousCommentsValueSchema = z
  .number()
  .int()
  .min(DANGEROUS_COMMENTS_MIN)
  .max(DANGEROUS_COMMENTS_MAX)
  .nullable();

export const boardDangerousCommentsSchema = z.object({
  boardId: ulidSchema,
  dangerousComments: dangerousCommentsValueSchema,
});

export type BoardDangerousComments = z.infer<typeof boardDangerousCommentsSchema>;

/** The body of `PATCH /boards/:id/dangerous-comments` — field-scoped, strict. */
export const boardDangerousCommentsPatchSchema = z.strictObject({
  dangerousComments: dangerousCommentsValueSchema,
});

export type BoardDangerousCommentsPatch = z.infer<
  typeof boardDangerousCommentsPatchSchema
>;

/**
 * `PATCH /boards/:id/members/:userId/role` — `FR-PERM-015`, "Admins can change
 * any board member's role". SC-4, field-scoped, and named to match
 * `workspaceRolePatchSchema`, which is the body of the exactly analogous
 * workspace route.
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ THIS BODY USED TO REFUSE `'observer'`, AND THE ARGUMENT FOR THE REFUSAL IS
 * REWRITTEN IN PLACE RATHER THAN DELETED — IT WAS RIGHT, AND ITS OWN STATED
 * CONDITION HAS BEEN MET
 * ---------------------------------------------------------------------------
 * What stood here from B09 through B37, verbatim, because a later reader
 * arriving from `BOARD_ROLES` or from `boardRoleCan` deserves to see what was
 * traded rather than a schema that looks as though it always admitted three
 * values:
 *
 * > **Two values in, three out.** `boardRoleSchema` admits `'observer'` because
 * > `ck_board_members_role` does and `boardMemberSchema` must serve a row
 * > holding it; this body does not, because B09 enforces nothing about an
 * > observer — `FR-PERM-016`'s exclusions are `BE-38.1`'s. Accepting
 * > `'observer'` here would store a role the product does not yet honour and
 * > hand the caller a success for a restriction that is not applied: an
 * > observer would still be able to write. A 422 is the honest answer **until
 * > `BE-38.1` lands**, and widening this enum is then a one-line change **with
 * > the exclusions beside it**.
 *
 * **`BE-38.1` has landed and the exclusions are beside it.** `FR-PERM-016` is
 * enforced in `requireBoardWriter` (`apps/server/src/boards/permissions.ts`) —
 * one predicate in one guard, which every other content guard in the server
 * already delegated to, so an observer is refused a card write, a list write, a
 * checklist tick and an attachment upload by a single edit rather than by six
 * copies that could drift. The role is therefore no longer "stored but not
 * honoured", which is the only thing this body was ever refusing, and the enum
 * below is now `boardRoleSchema` itself: **three in, three out**, the first
 * vocabulary in this file where the input and the view have converged.
 *
 * ⚠ **Do not read the convergence as a general tidying rule.** It happened
 * because a block built the missing enforcement, not because asymmetry is
 * untidy — `BE-67.1` widened `boardVisibilityPatchSchema` only after adding
 * `enterprise_visible`'s read arm and policy checks beside it, on the identical
 * argument this paragraph has just discharged for observers.
 *
 * ⚠ **The two things `BE-38.1` did NOT make reachable through this body.**
 * `FR-PERM-016`'s edge case — an observer who is separately a workspace admin
 * keeps the wider authority — is a fact about the *guard*, evaluated per
 * request against `workspace_members`, and there is nothing here to express it
 * with; and demoting the board's last admin to observer is refused by
 * `FR-PERM-032`'s last-admin rule as a **409** raised by the route, not as a
 * shape this schema could state (a Zod schema cannot know how many admins the
 * board currently holds). Both were true of `'normal'` before B38 and are
 * unchanged by it.
 *
 * The route this body belongs to changes the role of a member who is **already
 * on the board**; a `userId` that is not a member is a 404. Adding one is
 * `FR-PERM-018`/`BE-10.1`, and this must not become a back door to it — which
 * is also why this enum is `boardRoleSchema` and **not**
 * `GRANTABLE_BOARD_ROLES`: the two overlap on two values and differ on the
 * third in each direction, and sharing one tuple between them would either let
 * an add route mint an admin or stop a role patch from making one.
 */
export const boardRolePatchSchema = z.strictObject({
  role: boardRoleSchema.describe(
    'The board role to give this member. Admins can change board settings, ' +
      'including its visibility; a normal member reads and edits the board’s ' +
      'contents; an observer reads the board and is refused every write on it ' +
      '(FR-PERM-016), except that an observer who is separately a workspace ' +
      'admin keeps the wider workspace-admin authority. All three values are ' +
      'accepted — BE-38.1 built the observer exclusions, so the role this ' +
      'route stores is now a role the product honours.',
  ),
});

export type BoardRolePatch = z.infer<typeof boardRolePatchSchema>;

/**
 * `POST /boards/:id/members` — `FR-PERM-018`: "A board member with invite
 * rights (see `FR-PERM-027`) can add someone by email address or by username".
 *
 * **Structurally identical to `addWorkspaceMemberBodySchema` (`./workspace`),
 * and deliberately so**, down to reusing the same two primitives rather than
 * inventing a second spelling of the same rule: `emailSchema` and
 * `usernameSchema` both already live in `./user` — they are not local to
 * `./workspace`, so there is nothing here to "promote" later, only to import,
 * which is what this file does. A **union of two strict objects, not one
 * object with two optional fields**, for the reason `addWorkspaceMemberBodySchema`
 * gives in full: `{}` matches neither branch and `{ email, username }` matches
 * neither either, because `strictObject` refuses the extra key, so "neither
 * supplied" and "both supplied, disagreeing about who to add" are 422s at the
 * boundary rather than runtime questions the handler has to answer — and the
 * second one has no right answer.
 *
 * Neither branch lower-cases its input, matching `emailSchema`'s own note: a
 * schema that silently rewrote what it received would leave the wire shape and
 * the stored value disagreeing about what was sent. Case folding belongs at the
 * lookup, where `users.email`'s case-sensitive comparison (Postgres, no
 * collation override — Better Auth lower-cases on write, so the lookup must
 * fold to match it) is the thing actually being compensated for, not here.
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ THE `role` FIELD, AND WHY IT WAS ABSENT UNTIL B38 — REWRITTEN IN PLACE,
 * BECAUSE THE ABSENCE WAS ARGUED AND THE ARGUMENT'S CONDITION HAS BEEN MET
 * ---------------------------------------------------------------------------
 * `FR-PERM-018`'s behaviour text says "New members are added at normal level
 * **unless the inviter chooses observer**." Through B37 this body refused that
 * choice, and the refusal was reasoned rather than an omission. What stood
 * here, in short: accepting `'observer'` would let a caller *create* a role
 * that `FR-PERM-016`'s exclusions did not yet enforce — "an observer row is an
 * observer row regardless of which route inserted it, and `requireBoardReader`
 * treats it as a reader with full write access until `BE-38.1` lands" — so
 * admitting it here would have reopened, by design, the gap B09 was careful
 * only to leave reachable by accident.
 *
 * **`BE-38.1` has landed** (see `boardRolePatchSchema` above for what it built
 * and where), so the one condition that argument rested on is gone and the
 * field is here: optional, `GRANTABLE_BOARD_ROLES`, defaulting to `'normal'`.
 * That is `FR-PERM-018`'s own sentence read literally — the default is normal,
 * the choice is observer — and it is now a choice the product honours rather
 * than a value it merely stores.
 *
 * ⚠ **`'admin'` is NOT admissible**, which is why the field is
 * `grantableBoardRoleSchema` and not `boardRoleSchema`. Promotion belongs to
 * `PATCH /boards/:id/members/:userId/role` and `FR-PERM-015` AC3 keeps it
 * there; `GRANTABLE_BOARD_ROLES`' own docstring carries the full argument and
 * the second, sharper form of it that applies to an invite link. An `'admin'`
 * here is a 422 naming the field, not a silent downgrade to `'normal'` — a
 * caller who believes they have just made somebody an administrator must be
 * told they have not.
 *
 * ⚠ **The field is on BOTH arms, and it has to be.** This is a union of two
 * strict objects, so a `role` declared on the email arm alone would make
 * `{ username, role: 'observer' }` fail *both* branches — the username arm
 * refuses the unknown key and the email arm has no `email` — and the resulting
 * 422 would name neither the real problem nor the field. Adding a key to this
 * union means adding it twice, always, and there is no shared base to hang it
 * on precisely because `strictObject` is what makes "neither handle supplied"
 * and "both handles supplied" 422s at the boundary.
 *
 * ⚠ **`.default('normal')` makes `role` REQUIRED on the inferred type
 * (`AddBoardMemberBody`) while remaining optional on the wire.** That is Zod's
 * output type doing what it should — every parsed body carries a role, so no
 * handler has to spell the default a second time — and it is
 * `boardListQuerySchema`'s `.default('open')` precedent, not a new pattern. The
 * consequence to know before writing a fixture: an object literal typed as
 * `AddBoardMemberBody` must now include `role`, whereas the *request* need not.
 *
 * ---------------------------------------------------------------------------
 * WHAT ELSE THIS SHAPE DELIBERATELY DOES NOT DO
 * ---------------------------------------------------------------------------
 * `FR-PERM-018` also describes inviting someone with **no account** ("an
 * invitation is sent and membership is granted when they create an account")
 * and an **optional short message**. Neither is this shape's business: the
 * no-account path is `FR-PERM-019`/`FR-PERM-020` (the shareable link and the
 * pending invitation), owned by `BE-38.3`, on the exact precedent
 * `addWorkspaceMemberBodySchema` already set for the workspace route — B10
 * answers `404` naming the reason rather than writing a placeholder row nothing
 * else expects, and does not accept a `message` field with nowhere to store it.
 */
const addBoardMemberRoleField = grantableBoardRoleSchema
  .default('normal')
  .describe(
    'The board role to add this person at. Optional; “normal” when omitted, ' +
      'which is FR-PERM-018’s stated default. “observer” is the one ' +
      'alternative the requirement offers — an observer reads the board and ' +
      'is refused every write on it (FR-PERM-016). “admin” is refused with a ' +
      '422: promoting somebody is PATCH /boards/:id/members/:userId/role’s ' +
      'power and FR-PERM-015 AC3 keeps it there.',
  );

export const addBoardMemberBodySchema = z.union([
  z.strictObject({ email: emailSchema, role: addBoardMemberRoleField }),
  z.strictObject({ username: usernameSchema, role: addBoardMemberRoleField }),
]);

export type AddBoardMemberBody = z.infer<typeof addBoardMemberBodySchema>;

/**
 * The two surfaces a board list is asked for — `FR-EXP-012`.
 *
 * "Closing a board removes it from board lists, the workspace board grid and
 * default search results … Closed boards are listed on a dedicated 'Closed
 * boards' surface." Two surfaces, so two states, and no third: a *deleted* board
 * is not a state this enum can name, because `FR-EXP-012` AC3 requires its URL
 * to return not-found "for everyone" — it is absent from every read path rather
 * than filterable by one.
 */
export const BOARD_LIST_STATES = ['open', 'closed'] as const;

export const boardListStateSchema = z.enum(BOARD_LIST_STATES);

export type BoardListState = z.infer<typeof boardListStateSchema>;

/**
 * `GET /workspaces/:id/boards?state=open|closed` — `FR-EXP-012` AC1 — **and**,
 * since **B23**, `GET /boards?state=open|closed` (`FR-NOTIF-029`, `BE-23.1`),
 * reused **verbatim** rather than redeclared a second time.
 *
 * `open` by default, because that is the surface `FR-EXP-012` calls "the
 * workspace board list" and a caller who says nothing means the ordinary one.
 * `FR-NOTIF-029` AC3 draws the identical line at the landing page: absent from
 * the workspace groupings, present under Closed boards.
 *
 * A `strictObject`, which is unusual for a query string and deliberate. The
 * failure it prevents is the same silent-strip the request bodies guard against
 * and it is *worse* here, because a query key is easy to misremember:
 * `?status=closed` against a permissive schema returns **open** boards with a
 * 200, and the caller reads that as "this workspace has no closed boards". The
 * cost is that an unknown key — a link tracker's `utm_source`, a proxy's
 * addition — is a 422; acceptable, because the only client of this API is ours
 * and these paths are called by code, not typed into an address bar.
 *
 * ⚠ **This schema now guards two routes, and neither may declare a second
 * copy.** A permissive local re-declaration on either route would reopen the
 * exact silent-strip this schema exists to close, on one surface while the
 * other stayed guarded — see `packages/shared/src/boards-page.ts`'s header for
 * why `GET /boards` does not restate `{ state }` rather than importing this.
 */
export const boardListQuerySchema = z.strictObject({
  state: boardListStateSchema.default('open'),
});

export type BoardListQuery = z.infer<typeof boardListQuerySchema>;

// ---------------------------------------------------------------------------
// Views — what a route RETURNS.
//
// Width-bounded strings, no format checks, no transforms — see the note above
// the input primitives, and `./user`'s longer version of the same argument.
// ---------------------------------------------------------------------------

/**
 * A board as `POST /boards`, `GET /boards/:id`,
 * `GET /workspaces/:id/boards`, `PATCH /boards/:id/title`,
 * `POST /boards/:id/close` and `POST /boards/:id/reopen` all return it.
 *
 * Six fields, and each one is here because a requirement in this block needs it
 * rather than because the column exists:
 *
 *   - `id` — how every other route addresses the board.
 *   - `workspaceId` — `FR-BOARD-001` AC2, "the board is created in *that*
 *     workspace". It is also what a client needs to put the board back in a
 *     workspace context after resolving a short link, where the workspace was
 *     never part of the URL.
 *   - `title` — `FR-BOARD-003` AC1: the header, the boards list and the tab
 *     title all show it.
 *   - `slug` — **derived from the title at read time, not a column.**
 *     `FR-EXP-015` AC1 requires a client opening a stale URL to correct its
 *     address to the current slug, so the server has to state what "current"
 *     is. Derived rather than stored for the reason `initials` is derived in
 *     `./user` (when no superadmin override is set): a stored copy is a staler
 *     copy of the title, and the whole point
 *     is that a rename changes the slug immediately.
 *   - `shortLink` — `FR-EXP-015`. The permanent half of the address, and the
 *     only part that resolves.
 *   - `closed` — the lifecycle, below.
 *   - `visibility` — B09, and added here **because B08's own note asked for it
 *     in these words**: "`FR-EXP-016`'s share surface (V1) wants to state a
 *     board's visibility, and B09 should add the field with the routes that can
 *     change it, rather than inherit one that is always the default." Those
 *     routes now exist. Two requirements need it on the wire immediately, not
 *     at V1: `FR-PERM-012` AC1 says a normal member sees the visibility control
 *     "visible but not actionable", which means the client has to render the
 *     *current* level and grey the control rather than hide it (`00-`'s
 *     cross-cutting principle — gated capabilities are locked, never hidden),
 *     and `FE-09.1`'s picker cannot show a current selection without being told
 *     what it is.
 *
 * ---------------------------------------------------------------------------
 * WHY THE LIFECYCLE IS ONE BOOLEAN AND NOT TWO TIMESTAMPS OR A THREE-WAY ENUM
 * ---------------------------------------------------------------------------
 * `boards` carries `closed_at` and `deleted_at`, both nullable `timestamptz(3)`.
 * Neither reaches the wire.
 *
 * **`closedAt` is not served** because no requirement asks when a board was
 * closed. `FR-EXP-012` and `FR-BOARD-007` are written entirely in terms of open
 * versus closed — what it disappears from, what it appears under, what is
 * offered on it — and `FR-BOARD-008` gates deletion on "already closed", not on
 * how long ago. A timestamp on the wire is a field the web client may bind to
 * and a later block may not remove, and it would arrive in Postgres' own
 * spelling with the serialisation caveat `workspaceMemberSchema.joinedAt`
 * documents, for a value nothing renders.
 *
 * **`deletedAt` is not served** for a stronger reason: it could only ever be
 * `null`. D2 of the block brief makes permanent deletion a soft delete —
 * `fk_activity_entries_board_id` is `ON DELETE CASCADE`, so a hard delete would
 * take the board's whole activity history with it, which SC-1 exists to prevent
 * — and every read path therefore carries `deleted_at IS NULL`. A deleted board
 * 404s for everyone (`FR-EXP-012` AC3), so no response can ever carry a non-null
 * value. Serving it would invite a client to write `if (board.deletedAt)`, a
 * branch that is unreachable and that reads as though a restore path exists.
 * **There is no restore path and no route anywhere sets `deleted_at` back to
 * null.**
 *
 * A three-state enum (`'open' | 'closed' | 'deleted'`) is wrong for the same
 * reason: `'deleted'` is not observable, so the third member would be dead on
 * arrival and every consumer would carry a case that never fires.
 *
 * So one boolean, derived from `closed_at IS NOT NULL`, which is the whole of
 * what a caller can act on: show the board or list it under Closed boards, offer
 * Reopen, offer Permanently delete.
 */
export const boardSchema = z.object({
  id: ulidSchema,
  workspaceId: ulidSchema,
  title: z.string().max(BOARD_TITLE_MAX_LENGTH),
  /**
   * Derived (`slugify` in `./address`), so bounded by `SLUG_MAX_LENGTH` rather
   * than by any column. Not `.min(1)` — the derivation is total and the bound
   * that matters is the ceiling; a lower bound here would be a second place the
   * totality guarantee is stated, and the two could disagree.
   */
  slug: z.string().max(SLUG_MAX_LENGTH),
  shortLink: z.string().max(SHORT_LINK_MAX_LENGTH),
  /**
   * `boards.card_key` — the key every card reference on this board starts
   * with (`MG` in `MG-0001`), `docs/notes/card-keys.md`. Added **in place**,
   * on the `boardMemberSchema.isGuest` precedent: every view of a board should
   * carry it, and a key is a name, not a secret.
   *
   * ⚠ Carrying the key is not the same as being able to **use** it: a
   * reference resolves only for a caller who reads the board through
   * membership or workspace/Enterprise visibility, never through the public
   * arm. A client building a card path must not assume it may put the key in
   * one — see `boardContentsBoardSchema.cardRefsResolve`.
   */
  cardKey: z.string().max(BOARD_CARD_KEY_MAX_LENGTH),
  /** `boards.closed_at IS NOT NULL`. See the lifecycle note above. */
  closed: z.boolean(),
  /**
   * `boards.visibility` — `FR-PERM-008`–`FR-PERM-010`.
   *
   * All four levels, not the three `boardVisibilityPatchSchema` accepts; see
   * `BOARD_VISIBILITIES` for the rule and for why the asymmetry is deliberate
   * rather than an oversight to be tidied away.
   *
   * Worth stating because it is easy to misread as a leak: an **anonymous**
   * caller only ever sees the value `'public'` here. Anonymity survives exactly
   * one arm of the read scope, the public one, so a board this field could
   * describe to a signed-out reader is a board that is public by definition.
   */
  visibility: boardVisibilitySchema,
});

export type Board = z.infer<typeof boardSchema>;

export const boardListSchema = z.array(boardSchema);

/**
 * One row of a board's member list — `FR-PERM-015` AC1: "given a user creates a
 * board, when they open the member list, then they are shown as the board's
 * admin". Returned by `GET /boards/:id/members`, by
 * `PATCH /boards/:id/members/:userId/role` and — through
 * `addBoardMemberResultSchema.member` — by `POST /boards/:id/members`. **Three
 * consumers, not two**; count them before reasoning about a field added here.
 *
 * ⚠ **"Structurally identical to `workspaceMemberSchema`, and that is the
 * honest description of it" stood here from B09 to B37 and is corrected in
 * place at B38, because the vindication is the interesting half.** What it
 * said: both are `publicProfileSchema` plus a role plus a joined-at stamp, for
 * the same reasons in the same words — so this declaration existed for exactly
 * one difference, and inventing a second would have been worse than admitting
 * there was only one: **the role vocabularies are different closed sets.**
 * `ck_board_members_role` admits three values, `ck_workspace_members_role`
 * admits two, and `./workspace`'s `WORKSPACE_ROLES` already records that the
 * two role systems apply simultaneously and are deliberately separate. A single
 * shared member shape would have to type its `role` as the union of both
 * CHECKs, which would let a workspace route return `'observer'` and a board
 * route return a role its own constraint refuses — a runtime CHECK violation
 * that no compiler would catch.
 *
 * **B38 added `isGuest` here and nowhere else, so there are now two
 * differences and the shapes are no longer field-identical.** That is the
 * argument above paying off rather than an inconsistency introduced: had the
 * two been merged into one shared member schema on the strength of their
 * agreeing in B09, `FR-PERM-023`'s edge case would now have forced a field onto
 * the workspace member list where it is permanently `false` — a guest is by
 * definition somebody who is *not* in the workspace, so a workspace member row
 * can never carry a true one. Two declarations that agreed for twenty-eight
 * blocks and then diverged for a stated reason is exactly what a deliberately
 * separate vocabulary is for.
 *
 * The rest is `workspaceMemberSchema`'s reasoning inherited rather than
 * restated, and it is inherited by *extending the same base*:
 *
 *   - **`publicProfileSchema` is the base**, so a board's member list serves
 *     exactly "the portions of a profile another member may read"
 *     (`FR-PERM-002`) and cannot acquire an `email` by accident — `email` is not
 *     in the base, so adding it here would have to be deliberate and visible.
 *     Two member lists that each defined "public profile" for themselves would
 *     drift the next time either changed.
 *   - **`id` is the member's account id** (`users.id`), not a membership id:
 *     `board_members`'s primary key is `(board_id, user_id)` and there is no
 *     surrogate key. It is what `PATCH …/members/:userId/role` is addressed by,
 *     which is what lets a client act on a row it has just read.
 */
export const boardMemberSchema = publicProfileSchema.extend({
  /**
   * `board_members.role`, `CHECK role IN ('admin','normal','observer')`.
   *
   * All three on the way out, two on the way in — see `BOARD_ROLES` and
   * `boardRolePatchSchema`. A row holding `'observer'` is legal and this view
   * must serve it, even though no B09 route can create one.
   */
  role: boardRoleSchema,
  /**
   * `board_members.created_at`, serialised as the driver returns it.
   *
   * A bounded string rather than `z.iso.datetime()` or a coerced `Date`, for the
   * reason `workspaceMemberSchema.joinedAt` states at length: the column is
   * `timestamptz(3)` and `pg` hands it back in Postgres' own spelling
   * (`2026-08-10 12:00:00.123+00`), which is a legitimate instant and not an ISO
   * 8601 `T`/`Z` form, so pinning the serialisation here would make this schema
   * reject the value the database actually produced.
   *
   * Named `joinedAt` rather than `createdAt` for the same reason too: it is a
   * fact about the person, not about the row.
   */
  joinedAt: z.string().min(1),
  /**
   * **Is this member a guest — on this board but not in its workspace?**
   * `FR-PERM-023` (`BE-38.2`), whose edge case is the whole of the reason this
   * field exists: the board member list "must show guests **distinguishably**
   * from workspace members, so a board admin can tell who is external".
   *
   * ⚠⚠ **DERIVED, NEVER STORED, AND THERE IS NO `is_guest` COLUMN TO PULL IT
   * FROM.** `FR-PERM-023` defines a guest as "a member of a board but not of
   * that board's workspace" — a derivation over two membership tables, computed
   * as a `LEFT JOIN workspace_members … IS NULL` inside the member-list
   * statement that was already running, never a second query per member and
   * never a column. Storing it would create a second source of truth that goes
   * stale the instant somebody joins the workspace: `FR-PERM-021`'s edge case
   * ("a guest already on one board who is then added as a full member ceases to
   * be a guest") and `FR-PERM-022`'s approval both flip this value with no
   * write to any guest field anywhere. A stored copy would have to be
   * maintained by two routes that have no reason to know boards exist.
   *
   * ---------------------------------------------------------------------------
   * ⚠⚠ WHY THIS WIDENS A SHIPPED SCHEMA IN PLACE, WHICH `CLAUDE.md` NORMALLY
   * FORBIDS — DO NOT "FIX" IT ONTO A COPY
   * ---------------------------------------------------------------------------
   * The house rule is "extend a shipped schema on a copy, never widen it in
   * place", and the harm it names is precise: *one schema serialises several
   * routes, so widening changes responses that must not carry the field.* The
   * test is therefore whether some response served by this schema must **not**
   * carry `isGuest`, and all three of them must. `GET /boards/:id/members` is
   * the member list the edge case names outright.
   * `PATCH /boards/:id/members/:userId/role` returns the member as they now
   * stand, and a client that has just re-rendered one row from that response
   * would otherwise have to drop `isGuest` for that row alone — a list where
   * one entry silently loses its "external" marker after an unrelated edit.
   * `POST /boards/:id/members` returns the membership as it stands, including
   * on the already-a-member no-op path, where "you added somebody who is
   * external to this workspace" is exactly the feedback `FR-PERM-018`'s edge
   * case asks for. Three subjects, one subject: **a board member.**
   *
   * ⚠ **The objection a reader will reach for is `./watch`'s, and it does not
   * transfer.** That file refuses a `watching` boolean on `cardDetailSchema`
   * because the field would be a fact about the **viewer**, and
   * `cardDetailSchema` also serialises an anonymous-capable read where a
   * per-viewer boolean has no truthful value. `isGuest` is a fact about the
   * **listed member** — it is the same value whoever is asking, and none of the
   * three routes above is anonymous-capable (all three sit behind
   * `requireBoardReader` or stricter). There is no viewer in this field.
   *
   * ⚠ **It is NOT a fourth board role and must not become one.**
   * `ck_board_members_role` admits three values and `BOARD_ROLES` states them;
   * "guest" is orthogonal to all three — a guest may be a board **admin**,
   * which is precisely the hole `FR-PERM-023` AC4 closes on
   * `POST /boards/:id/template`. Folding guest-ness into `role` would make that
   * combination inexpressible and would need a fourth CHECK member for a fact
   * no row holds.
   *
   * ⚠ **`workspaceMemberSchema` does NOT gain a mirror of this**, so the two
   * member views are no longer field-identical and the assertion that they were
   * is retired rather than preserved. A workspace member is by definition not a
   * guest of their own workspace; the field would be permanently `false` there,
   * which is the dead-on-arrival shape `boardSchema`'s lifecycle note rejects
   * for `deletedAt`. `FR-PERM-024`'s "shown as a multi-board guest" on the
   * *workspace* member list is a different fact on a different subject, is
   * Parity-Later, and no `BE-38.x` item owns it — do not build it here.
   */
  isGuest: z.boolean(),
});

export type BoardMember = z.infer<typeof boardMemberSchema>;

/**
 * `boardMemberSchema` plus the handles the member has relinquished.
 *
 * A COPY, never a widening of `boardMemberSchema` in place: that schema
 * serialises every member read in the product, and most of them have no
 * business carrying history. Only the board member LIST — the surface
 * `commentMentionSegments` resolves `@` text against — uses this, so an
 * older comment keeps linking to a renamed member wherever the list reaches.
 *
 * `formerUsernames` is OPTIONAL, not defaulted: readers that never populate
 * it (older responses, other producers) still parse, and a plain
 * `BoardMember` remains assignable to `BoardMemberWithHistory`, so no
 * existing consumer needs a rewrite to keep compiling.
 *
 * BE-44.6, `FR-NOTIF-033` AC3. Populated from `user_username_history` by
 * `GET /boards/:id/members`.
 */
export const boardMemberWithHistorySchema = boardMemberSchema.extend({
  formerUsernames: z.array(z.string().max(USERNAME_MAX_LENGTH)).optional(),
});

export type BoardMemberWithHistory = z.infer<typeof boardMemberWithHistorySchema>;

export const boardMemberListSchema = z.array(boardMemberWithHistorySchema);

/**
 * `POST /boards/:id/members` — `FR-PERM-018`'s edge case: "Adding a person who
 * is already a member must be a no-op with clear feedback, not a duplicate
 * entry."
 *
 * **Field-identical to `addWorkspaceMemberResultSchema` (`./workspace`), and
 * the argument for the shape is the one recorded there, inherited rather than
 * restated**: both halves of the no-op sentence need a place to live. `added`
 * is how a caller tells "I added them" from "they were already here" — both are
 * 200s, so the status code cannot carry the difference — and `member` is the
 * membership **as it stands**, which on the no-op path is the row that was
 * already there with the role it already had. Declared here rather than reused
 * from `./workspace` for the reason `boardMemberSchema` already gives for
 * itself: the two member views are field-identical and still two declarations,
 * because their `role` is two different closed sets, and a shared result shape
 * would have to hold whichever member schema is correct for its route — which
 * is exactly the coupling `boardMemberSchema`'s docstring rejects.
 *
 * `member` is `boardMemberSchema`, not a narrower "just added" projection: a
 * caller re-adding a promoted colleague (`added: false`) needs to see that the
 * promotion survived rather than being silently reset to `'normal'` — the same
 * concern `addWorkspaceMemberResultSchema`'s docstring states, and the reason
 * D4 of the block brief insists on `ON CONFLICT DO NOTHING`, never
 * `DO UPDATE`, at the route this schema serves.
 *
 * Declared here, after `boardMemberListSchema` and not beside
 * `addBoardMemberBodySchema` above, because it **references** `boardMemberSchema`
 * — a `const` used before its declaration is a runtime `ReferenceError` in this
 * module's evaluation order, not a type error, so the ordering is load-bearing
 * and not cosmetic. `addWorkspaceMemberResultSchema` sits in the identical
 * position in `./workspace`, last in the file, after `workspaceMemberSchema`.
 */
export const addBoardMemberResultSchema = z.object({
  added: z.boolean(),
  member: boardMemberSchema,
});

export type AddBoardMemberResult = z.infer<typeof addBoardMemberResultSchema>;

/**
 * What `GET /boards/by-short-link/:shortLink` returns — `FR-EXP-015`.
 *
 * The board, plus the address the caller should now be at. The board's own
 * `slug` is what `FR-EXP-015` AC1 literally names ("the address is corrected to
 * the new slug") and `path` is that slug already joined to the prefix and the
 * short link. Both, deliberately: the assembled path is what the client actually
 * pushes into its history, and having the server state it means the URL grammar
 * has one producer rather than one producer and one re-implementation that drift
 * apart silently — the segment is never parsed, so nothing would ever fail.
 *
 * Built by extending `boardSchema` rather than nesting it, because the resolver
 * answers the same question `GET /boards/:id` answers plus one more, and a
 * caller should be able to hand the result to anything that takes a board.
 */
export const boardAddressSchema = boardSchema.extend({
  /** `/b/<shortLink>/<slug>` — `boardPath()` in `./address`. */
  path: z.string().min(1),
});

export type BoardAddress = z.infer<typeof boardAddressSchema>;

/**
 * What `GET /cards/by-short-link/:shortLink` returns — `FR-EXP-015`,
 * `FR-CARD-005`.
 *
 * **This is an address, not a card.** `BE-12.1` owns the card contract and this
 * shape must not grow towards it: there is no description, no labels, no
 * members, no dates and no comments here, and adding one would make `./board`
 * the place the card model accidentally lives. What it carries is exactly what
 * `FR-CARD-005` calls the address — "a globally unique short link token and a
 * board-scoped sequential card number … the permalink combines them with a slug
 * of the title" — and the title, because the slug is derived from it and a
 * caller correcting its address bar needs to see what it was derived from.
 *
 * `board` is nested and is the full board view, because of `FR-EXP-015` AC2:
 * "given a card URL opened directly, then the card detail is shown with its
 * board visible behind it, and closing the card leaves the user on that board".
 * A caller given only `boardId` would have to make a second request to learn the
 * board's title and short link before it could render or leave, on the critical
 * path of every pasted card link. Nesting the view rather than copying two
 * fields out of it also means the board a card resolves through is described by
 * the same schema as a board resolved directly.
 *
 * ---------------------------------------------------------------------------
 * WHY `boardId` AND `cardNumber` ARE NOT NULLABLE HERE, THOUGH THE COLUMNS ARE
 * ---------------------------------------------------------------------------
 * `cards.board_id` and `cards.card_number` are both nullable: a card in a
 * personal inbox belongs to no board and holds no board-scoped number. That is a
 * real state and this shape deliberately cannot express it, because
 * `FR-CARD-005`'s permalink has no meaning without both halves and
 * `FR-EXP-015` AC2 promises a board behind the card. The resolver therefore
 * answers **404 with an explanation** for a card that has no board — the same
 * shape `FR-EXP-015`'s own edge case prescribes for a deleted card ("returns
 * not-found with an explanation that the card no longer exists") — rather than
 * serving a half-populated address. The personal inbox is
 * `09-notifications-and-personal-surfaces.md`'s surface and B29's; when it lands
 * it may need an address of its own, and that is a decision for the block that
 * builds it, not a nullable field added here in advance.
 */
export const cardAddressSchema = z.object({
  id: ulidSchema,
  shortLink: z.string().max(SHORT_LINK_MAX_LENGTH),
  /** `cards.card_number`, board-scoped and never reused — `FR-CARD-005`. */
  cardNumber: cardNumberSchema,
  /** `KEY-NNNN`, or `null` exactly when `path` carries the short link — `cardReferenceSchema` in `./board`. */
  ref: cardReferenceSchema.nullable(),
  title: z.string().max(CARD_TITLE_MAX_LENGTH),
  /** Derived from `title` by `slugify` in `./address`; see `boardSchema.slug`. */
  slug: z.string().max(SLUG_MAX_LENGTH),
  /** `/c/<KEY-NNNN>/<slug>`, or `/c/<shortLink>/<slug>` for a caller who cannot resolve references — `cardPath()` in `./address`. */
  path: z.string().min(1),
  /** The board the card is on **now**, which a cross-board move changes. */
  board: boardSchema,
});

export type CardAddress = z.infer<typeof cardAddressSchema>;
